import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Loading } from '@arcflight/tf-component-library';
import { useDispatch } from 'react-redux';
import TFInput from '../TFInput/TFInput';
import FileViewer from '../TFFileViewer/fileViewer';
import { getMxActions, postMxAction } from '../../services/apiNew';
import useDraftAttachments from '../../services/hooks/attachments/useDraftAttachments';
import { MxEventType } from '../../models/workpacks';
import { ButtonSize } from '../PaginatedDefectsTable/DefectTableHeader';
import { addToast, ToastCategories, ToastTypes } from '../../models/toasts';
import { AircraftPermission, AircraftResource } from '../../models/aircraft';
import { AircraftAuthenticationWrapper } from '../_utils/AuthenticationWrapper';
import useGetOperatorSetting from '../../utils/useGetOperatorSetting';
import pluralizeOperatorTerminology from '../../utils/pluralizeOperatorTerminology';
import ActionCard from './ActionCard';
import usePersistMxActionUpdate from './usePersistMxActionUpdate';
import usePersistMxActionDelete from './usePersistMxActionDelete';

interface MxItemActionsProps {
  mxItemId: string;
  aircraftId: string;
  dateFormat: string;
  resource: AircraftResource;
  isResolved?: boolean;
  addActionsOnDefects: boolean;
}

const ActionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ActionsTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #242d41;
`;

const ActionsCount = styled.span`
  font-size: 14px;
  color: rgba(36, 45, 65, 0.7);
`;

const ActionsDisabledMessage = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(36, 45, 65, 0.7);
`;

const FormBorder = styled.div`
  border: 1px solid rgba(36, 45, 65, 0.15);
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 20px;
  background-color: #fcfcfc;
`;

const FormRow = styled.div`
  margin-bottom: 12px;
  &:last-of-type {
    margin-bottom: 16px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.4px;
  color: ${({ theme }): string => theme.colours.graphite};
  margin-bottom: 4px;
  line-height: 1.33;
`;

const StyledTextarea = styled.textarea`
  border-radius: 4px;
  border-color: rgba(36, 45, 65, 0.1);
  padding: 8px;
  width: 100%;
`;

const RequiredAsterisk = styled.span`
  color: ${({ theme }): string => theme.colours.blue};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const MxItemActions: React.FC<MxItemActionsProps> = ({
  mxItemId,
  aircraftId,
  dateFormat,
  resource,
  isResolved = false,
  addActionsOnDefects = false,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const actionSingular =
    useGetOperatorSetting('action_name', aircraftId) ?? formatMessage({ id: 'text.action' });
  const actionPlural = pluralizeOperatorTerminology(actionSingular);
  const actionTerminology = { actionSingular, actionPlural };
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [activeEditActionId, setActiveEditActionId] = useState<string | null>(null);
  const {
    draftAttachments: createAttachments,
    addDraftAttachment: addCreateAttachment,
    handleDeleteClick: removeCreateAttachment,
    resetDraftAttachments: resetCreateAttachments,
  } = useDraftAttachments({ mode: 'create' });

  const canEditActions = addActionsOnDefects && !isResolved;

  const { data: actions = [], isLoading } = useQuery({
    queryKey: ['mxActions', mxItemId],
    queryFn: async () => {
      const res = await getMxActions(mxItemId);
      return res.data;
    },
    enabled: !!mxItemId,
  });

  const handleAddAction = async () => {
    const trimmedTitle = title.trim();
    if (submitting) return;
    if (!trimmedTitle) {
      setTitleError(formatMessage({ id: 'validation.actionTitle.required' }, actionTerminology));
      return;
    }
    setTitleError(null);
    setSubmitting(true);
    try {
      const newFiles = createAttachments
        .filter((a) => a.pendingFile)
        .map((a) => {
          const f = a.pendingFile as File;
          return { file: f, file_name: a.file_name ?? f.name };
        });
      await postMxAction(mxItemId, {
        event_type: MxEventType.ACTION,
        title: trimmedTitle,
        description: description.trim() || undefined,
        ...(newFiles.length ? { attachments: newFiles } : {}),
      });
      setTitle('');
      setDescription('');
      resetCreateAttachments([]);
      queryClient.invalidateQueries({ queryKey: ['mxActions', mxItemId] });
    } catch (err) {
      const errorData =
        typeof axios.isAxiosError === 'function' && axios.isAxiosError(err)
          ? err.response?.data
          : (err as { response?: { data?: { error?: unknown } } })?.response?.data;
      const message = errorData?.error != null
        ? String(errorData.error)
        : formatMessage({ id: 'text.unableToCreateAction' }, actionTerminology);
      dispatch(
        addToast({
          payload: {
            title: formatMessage({ id: 'text.actionCreationError' }, actionTerminology),
            message,
            type: ToastTypes.ERROR,
            category: ToastCategories.FLAG,
          },
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const persistActionUpdate = usePersistMxActionUpdate(mxItemId, actionSingular);

  const persistActionDelete = usePersistMxActionDelete(mxItemId, actionSingular);

  const count = actions.length;

  return (
    <div data-testid="MxItemActions">
      <ActionsHeader>
        <ActionsTitle>{formatMessage({ id: 'text.operationalActions' }, actionTerminology)}</ActionsTitle>
        <ActionsCount data-testid="MxItemActions-count">
          {formatMessage({ id: 'text.actionsLoggedCount' }, { count, ...actionTerminology })}
        </ActionsCount>
      </ActionsHeader>

      {count === 0 && !addActionsOnDefects && !isLoading && (
        <ActionsDisabledMessage data-testid="MxItemActions-disabledMessage">
          {formatMessage({ id: 'text.addActionsOnDefectDisabled' }, actionTerminology)}
        </ActionsDisabledMessage>
      )}

      {addActionsOnDefects && !isResolved && (
        <AircraftAuthenticationWrapper
          aircraftId={aircraftId}
          requiredResource={resource}
          requiredPermissionLevel={AircraftPermission.CREATE}
        >
          <FormBorder>
            <FormRow>
              <FieldLabel htmlFor="action-title">
                {formatMessage({ id: 'text.title' })} <RequiredAsterisk>*</RequiredAsterisk>
              </FieldLabel>
              <TFInput
                id="action-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (titleError) setTitleError(null);
                }}
                status={titleError ? 'error' : undefined}
                statusMessage={titleError ?? ''}
              />
            </FormRow>
            <FormRow>
              <FieldLabel htmlFor="action-description">{formatMessage({ id: 'text.description' })}</FieldLabel>
              <StyledTextarea
                id="action-description"
                name="action-description"
                data-testid="MxItemActions-description"
                rows={5}
                value={description}
                onChange={(e): void => setDescription(e.target.value)}
              />
            </FormRow>
            <FormRow>
              <FieldLabel>{formatMessage({ id: 'text.attachments' })}</FieldLabel>
              <FileViewer
                attachments={createAttachments}
                addAttachment={addCreateAttachment}
                handleDeleteClick={removeCreateAttachment}
                allowDelete={!submitting}
                disableAddition={submitting}
                fileInputId="mx-action-create-file-input"
              />
            </FormRow>
            <ButtonWrapper>
              <Button
                size={ButtonSize.MEDIUM}
                onClick={handleAddAction}
                disabled={submitting}
                data-testid="MxItemActions-addButton"
              >
                {formatMessage({ id: 'text.actionAddButton' }, actionTerminology)}
              </Button>
            </ButtonWrapper>
          </FormBorder>
        </AircraftAuthenticationWrapper>
      )}

      {isLoading ? (
        <Loading loading={isLoading} />
      ) : (
        actions.map((action) => (
          <ActionCard
            key={action.id}
            action={action}
            dateFormat={dateFormat}
            aircraftId={aircraftId}
            resource={resource}
            actionNameSingular={actionSingular}
            canEdit={canEditActions}
            activeEditActionId={activeEditActionId}
            onActiveEditChange={setActiveEditActionId}
            onPersistUpdate={persistActionUpdate}
            onPersistDelete={persistActionDelete}
          />
        ))
      )}
    </div>
  );
};

export default MxItemActions;